import { useId } from 'react';

import type { Group } from './groupsApi.ts';

interface GroupSwitcherProps {
  groups: Group[];
  selected: Group;
  onSelect: (groupId: string) => void;
}

/**
 * Switches between groups.
 *
 * A native <select> rather than a custom dropdown: it is operable one-handed, gets the
 * platform's own picker on a phone, and is accessible without any work on our part. A
 * bespoke menu here would be worse in every way that matters in a shop.
 *
 * Hidden entirely when there is only one group, which is the common case — a switcher
 * with one option is just clutter at the top of a screen that needs to stay calm.
 */
export function GroupSwitcher({ groups, selected, onSelect }: GroupSwitcherProps) {
  const id = useId();

  if (groups.length < 2) {
    return <h1 className="group-title">{selected.name}</h1>;
  }

  return (
    <div className="group-switcher">
      <label className="visually-hidden" htmlFor={id}>
        Group
      </label>
      <select
        id={id}
        className="group-switcher__select"
        value={selected.id}
        onChange={(event) => onSelect(event.target.value)}
      >
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </select>
    </div>
  );
}
